import { useState, useCallback } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../config/firebase";
import { ROLES } from "../constants";

export const useSearch = (user) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // ===== GỢI Ý THEO VAI TRÒ =====
  const getSuggestions = useCallback(() => {
    const userRoles = user?.vaiTro || [];
    const isAdmin = userRoles.includes(ROLES.ADMIN);
    const isTruongNganh = userRoles.includes(ROLES.TRUONG_NGANH);
    const isGVCN = userRoles.includes(ROLES.GVCN);
    const isGVPhu = userRoles.includes(ROLES.GV_PHU);

    const suggestions = [];

    // Tất cả đều thấy Dashboard
    suggestions.push({
      id: 'dashboard',
      type: 'quick',
      label: 'Dashboard',
      subtitle: 'Xem tổng quan',
      path: '/dashboard',
      icon: 'dashboard',
    });

    // GVCN + GV Phụ: Điểm danh, Nhập điểm
    if (isGVCN || isGVPhu) {
      suggestions.push({
        id: 'diem-danh',
        type: 'quick',
        label: 'Điểm danh hôm nay',
        subtitle: 'Điểm danh lớp học',
        path: '/diem-danh',
        icon: 'fact_check',
      });
      suggestions.push({
        id: 'nhap-diem',
        type: 'quick',
        label: 'Nhập điểm',
        subtitle: 'Cập nhật điểm số',
        path: '/nhap-diem',
        icon: 'edit_note',
      });
    }

    // GVCN + Admin: Đánh giá cuối kỳ
    if (isGVCN || isAdmin) {
      suggestions.push({
        id: 'danh-gia',
        type: 'quick',
        label: 'Đánh giá cuối kỳ',
        subtitle: 'Đánh giá học sinh',
        path: '/danh-gia-cuoi-ky',
        icon: 'grading',
      });
    }

    // Admin: Quản lý người dùng, Cài đặt
    if (isAdmin) {
      suggestions.push({
        id: 'quan-ly-nguoi-dung',
        type: 'quick',
        label: 'Quản lý người dùng',
        subtitle: 'Thêm/sửa/xóa tài khoản',
        path: '/quan-ly-nguoi-dung',
        icon: 'manage_accounts',
      });
      suggestions.push({
        id: 'cai-dat',
        type: 'quick',
        label: 'Cài đặt hệ thống',
        subtitle: 'Quản lý ngành, lớp, giáo họ',
        path: '/cai-dat',
        icon: 'settings',
      });
    }

    // Admin + Trưởng ngành: Báo cáo
    if (isAdmin || isTruongNganh) {
      suggestions.push({
        id: 'bao-cao',
        type: 'quick',
        label: 'Xem báo cáo',
        subtitle: 'Thống kê, báo cáo',
        path: '/bao-cao',
        icon: 'monitoring',
      });
    }

    return suggestions;
  }, [user]);

  // ===== TÌM KIẾM =====
  const search = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 1) {
      setResults([]);
      return;
    }

    setLoading(true);
    const query_lower = searchQuery.toLowerCase().trim();
    const allResults = [];

    try {
      const userRoles = user?.vaiTro || [];
      const isAdmin = userRoles.includes(ROLES.ADMIN);
      const isTruongNganh = userRoles.includes(ROLES.TRUONG_NGANH);
      const isGVCN = userRoles.includes(ROLES.GVCN);
      const isGVPhu = userRoles.includes(ROLES.GV_PHU);
      const lopCN = user?.maLopCN;
      const maNganh = user?.maNganh;

      // ===== 1. Tìm HỌC SINH =====
      if (isAdmin || isTruongNganh || isGVCN || isGVPhu) {
        let hsSnapshot;
        
        if (isAdmin || isTruongNganh) {
          hsSnapshot = await getDocs(collection(db, "hocSinh"));
        } else {
          if (lopCN) {
            const q = query(collection(db, "hocSinh"), where("lopHienTaiId", "==", lopCN));
            hsSnapshot = await getDocs(q);
          } else {
            hsSnapshot = { docs: [] };
          }
        }

        hsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const hoTen = (data.hoTen || '').toLowerCase();
          const tenThanh = (data.tenThanh || '').toLowerCase();
          const hoTenBo = (data.hoTenBo || '').toLowerCase();
          const hoTenMe = (data.hoTenMe || '').toLowerCase();

          if (hoTen.includes(query_lower) || tenThanh.includes(query_lower) || 
              hoTenBo.includes(query_lower) || hoTenMe.includes(query_lower)) {
            allResults.push({
              id: doc.id,
              type: 'hocSinh',
              label: data.hoTen || 'Không tên',
              subtitle: `${data.tenThanh ? 'Thánh ' + data.tenThanh + ' • ' : ''}${data.lopHienTaiId || 'Chưa xếp lớp'}`,
              path: `/hoc-sinh/${doc.id}`, // ✅ Vào thẳng hồ sơ HS
              icon: 'person',
              color: 'text-primary',
              bg: 'bg-primary-fixed',
            });
          }
        });
      }

      // ===== 2. Tìm NGƯỜI DÙNG =====
      if (isAdmin) {
        const ndSnapshot = await getDocs(collection(db, "nguoiDung"));
        ndSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const hoTen = (data.hoTen || '').toLowerCase();
          const email = (data.email || '').toLowerCase();
          const tenThanh = (data.tenThanh || '').toLowerCase();

          if (hoTen.includes(query_lower) || email.includes(query_lower) || tenThanh.includes(query_lower)) {
            allResults.push({
              id: doc.id,
              type: 'nguoiDung',
              label: data.hoTen || email || 'Không tên',
              subtitle: `${data.vaiTro?.join(', ') || '...'} • ${data.trangThai === 'nghi' ? 'Đã nghỉ' : 'Đang hoạt động'}`,
              path: `/quan-ly-nguoi-dung?uid=${doc.id}`, // ✅ Vào trang QLND với query param
              icon: 'manage_accounts',
              color: 'text-secondary',
              bg: 'bg-secondary-fixed',
            });
          }
        });
      }

      // ===== 3. Tìm LỚP HỌC =====
      if (isAdmin || isTruongNganh) {
        const lopSnapshot = await getDocs(collection(db, "lopHoc"));
        lopSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const tenLop = (data.tenLop || '').toLowerCase();
          const maLop = (data.maLop || '').toLowerCase();

          if (isTruongNganh && maNganh) {
            const nganhId = data.nganhId || '';
            if (nganhId !== maNganh) return;
          }

          if (tenLop.includes(query_lower) || maLop.includes(query_lower)) {
            allResults.push({
              id: doc.id,
              type: 'lopHoc',
              label: data.tenLop || maLop || 'Không tên',
              subtitle: `Mã lớp: ${data.maLop || '...'}`,
              path: `/quan-ly-hoc-sinh?lop=${doc.id}&tenLop=${encodeURIComponent(data.tenLop || '')}`, // ✅ Vào QLHS với filter lớp
              icon: 'school',
              color: 'text-tertiary',
              bg: 'bg-tertiary-fixed',
            });
          }
        });
      }

      setResults(allResults.slice(0, 10));
    } catch (error) {
      console.error("Lỗi tìm kiếm:", error);
      setResults([]);
    }

    setLoading(false);
  }, [user]);

  return { results, loading, search, getSuggestions };
};